import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from './ui/tooltip';
import { Collapsible, CollapsibleContent } from './ui/collapsible';
import {
  Search,
  RefreshCw,
  X,
  Filter,
  ChevronDown,
  ChevronUp,
  Download,
  SlidersHorizontal,
  Loader2,
  Ruler
} from 'lucide-react';

// Custom Combobox for autocomplete search
const SearchCombobox = ({
  value,
  onChange,
  options,
  loading,
  placeholder
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          placeholder={placeholder}
          className="pl-10"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>
      {isOpen && options.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
          {options.map((option, index) => (
            <div
              key={index}
              className="px-3 py-2 cursor-pointer hover:bg-muted"
              onMouseDown={() => {
                onChange(typeof option === 'string' ? option : option.value);
                setIsOpen(false);
              }}
            >
              <p className="text-sm">
                {typeof option === 'string' ? option : option.value}
              </p>
              {typeof option === 'object' && option.category && (
                <p className="text-xs text-muted-foreground">
                  {option.category} • ID: {option.productId}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Category Combobox for autocomplete category selection
const CategoryCombobox = ({
  value,
  onChange,
  options,
  onClear
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');

  const filteredOptions = options.filter(opt =>
    opt.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div className="relative min-w-[200px]">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onChange(e.target.value);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          placeholder="按分类筛选..."
          className="pl-10 pr-10"
        />
        {value && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
            onClick={(e) => {
              e.stopPropagation();
              setInputValue('');
              onClear();
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              className="px-3 py-2 cursor-pointer hover:bg-muted text-sm"
              onMouseDown={() => {
                setInputValue(option);
                onChange(option);
                setIsOpen(false);
              }}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdvancedSearch = ({
  // Data props
  searchMode = 'basic',
  filters,
  categories = [],
  searchSuggestions = [],
  searchLoading = false,
  globalSearch = '',

  // State handlers
  onSearchModeToggle,
  onFiltersChange,
  onAdvancedFilterChange,
  onGlobalSearchChange,
  onFilterSubmit,
  onClearFilter,
  onClearAllFilters,
  onExport,

  // UI state
  advancedFiltersOpen,
  setAdvancedFiltersOpen,
  loading = false,

  // Configuration
  searchPlaceholder = "按产品名称、编号、材料或规格搜索...",
  showAdvancedToggle = true,
  showExportButton = true,
  customAdvancedFields = [],
  pageType = 'products',

  // Admin features
  userRole = '',
  tradeTypeFilter = 'all',
  onTradeTypeFilterChange,
  onSearchBySpecification
}) => {
  // Active filters count for badge
  // Active filters count for badge
  const activeFiltersCount = React.useMemo(() => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'price_range' || key === 'tip_diameter_range' || key === 'total_length_range' || key === 'degree_range') {
        if (value?.min || value?.max) count++;
      } else if (value) {
        count++;
      }
    });
    if (globalSearch.trim()) count++;
    return count;
  }, [filters, globalSearch]);

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        {/* Global Search Bar */}
        {searchMode === 'advanced' && (
          <div className="mb-4">
            <SearchCombobox
              value={globalSearch}
              onChange={onGlobalSearchChange}
              options={searchSuggestions}
              loading={searchLoading}
              placeholder={searchPlaceholder}
            />
          </div>
        )}

        {/* Search by Specification Button */}
        {pageType === 'products' && onSearchBySpecification && (
          <div className="mb-4 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onSearchBySpecification}
              className="text-muted-foreground hover:text-primary"
            >
              <Ruler className="mr-2 h-4 w-4" />
              使用规格搜索
            </Button>
          </div>
        )}

        {/* Filter Controls Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">搜索和筛选</h3>
            <Badge variant={searchMode === 'advanced' ? 'default' : 'secondary'}>
              {searchMode === 'basic' ? '基础' : '高级'}
            </Badge>
            {activeFiltersCount > 0 && (
              <div className="relative">
                <Filter className="h-4 w-4" />
                <Badge
                  variant="default"
                  className="absolute -top-2 -right-2 h-4 w-4 p-0 flex items-center justify-center text-xs"
                >
                  {activeFiltersCount}
                </Badge>
              </div>
            )}
          </div>
          <div className="flex gap-1">
            {showAdvancedToggle && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={onSearchModeToggle}>
                      <SlidersHorizontal className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    切换到{searchMode === 'basic' ? '高级' : '基础'}搜索
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setAdvancedFiltersOpen(!advancedFiltersOpen)}
                  >
                    {advancedFiltersOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>展开/收起高级筛选</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {showExportButton && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={onExport}>
                      <Download className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>导出当前结果</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        {/* Basic Filters */}
        <form
          className="flex flex-wrap gap-3 items-center"
          onSubmit={(e) => { e.preventDefault(); onFilterSubmit(); }}
        >
          <div className="relative min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              name="name"
              value={filters.name}
              onChange={onFiltersChange}
              placeholder={`按${pageType === 'inventory' ? '物品' : '产品'}名称筛选...`}
              className="pl-10 pr-10"
            />
            {filters.name && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                onClick={() => onClearFilter('name')}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>

          <CategoryCombobox
            value={filters.category}
            onChange={(val) => onAdvancedFilterChange('category', val)}
            options={categories}
            onClear={() => onClearFilter('category')}
          />

          <div className="relative min-w-[200px]">
            <Input
              name="product_id"
              value={filters.product_id}
              onChange={onFiltersChange}
              placeholder={`按${pageType === 'inventory' ? '物品' : '产品'}编号筛选...`}
              className="pr-10"
            />
            {filters.product_id && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                onClick={() => onClearFilter('product_id')}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>

          <div className="relative min-w-[200px]">
            <Input
              name="product_code"
              value={filters.product_code || ''}
              onChange={onFiltersChange}
              placeholder="按产品代码筛选..."
              className="pr-10"
            />
            {filters.product_code && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                onClick={() => onClearFilter('product_code')}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>

          <div className="relative min-w-[200px]">
            <Input
              name="part_number"
              value={filters.part_number}
              onChange={onFiltersChange}
              placeholder="按零件号筛选..."
              className="pr-10"
            />
            {filters.part_number && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                onClick={() => onClearFilter('part_number')}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>

          <div className="relative min-w-[200px]">
            <Input
              name="client_ref_number"
              value={filters.client_ref_number}
              onChange={onFiltersChange}
              placeholder="按物料编码筛选..."
              className="pr-10"
            />
            {filters.client_ref_number && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                onClick={() => onClearFilter('client_ref_number')}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>

          <Button type="submit" variant="ghost" size="icon" disabled={loading} title="刷新">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClearAllFilters}
            disabled={loading}
            title="清除所有筛选"
          >
            <X className="h-4 w-4" />
          </Button>
        </form>

        {/* Advanced Filters */}
        <Collapsible open={advancedFiltersOpen}>
          <CollapsibleContent>
            <Separator className="my-4" />
            <p className="text-sm font-medium mb-4">高级筛选</p>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>材质</Label>
                <Select
                  value={filters.material || 'all'}
                  onValueChange={(value) => onAdvancedFilterChange('material', value === 'all' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部材质" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部材质</SelectItem>
                    <SelectItem value="钢">钢</SelectItem>
                    <SelectItem value="不锈钢">不锈钢</SelectItem>
                    <SelectItem value="铝">铝</SelectItem>
                    <SelectItem value="铜">铜</SelectItem>
                    <SelectItem value="塑料">塑料</SelectItem>
                    <SelectItem value="陶瓷">陶瓷</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>涂层</Label>
                <Input
                  value={filters.coating}
                  onChange={(e) => onAdvancedFilterChange('coating', e.target.value)}
                  placeholder="例如: 镀锌, 阳极氧化"
                />
              </div>

              <div className="space-y-2">
                <Label>{pageType === 'inventory' ? '库存状态' : '产品状态'}</Label>
                <Select
                  value={filters.stock_status || 'all'}
                  onValueChange={(value) => onAdvancedFilterChange('stock_status', value === 'all' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部状态" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部状态</SelectItem>
                    <SelectItem value="有库存">有库存</SelectItem>
                    <SelectItem value="库存不足">库存不足</SelectItem>
                    <SelectItem value="无库存">无库存</SelectItem>
                    <SelectItem value="按订单生产">按订单生产</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>最低价格 (¥)</Label>
                <Input
                  type="number"
                  value={filters.price_range?.min || ''}
                  onChange={(e) => onAdvancedFilterChange('price_range.min', e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label>最高价格 (¥)</Label>
                <Input
                  type="number"
                  value={filters.price_range?.max || ''}
                  onChange={(e) => onAdvancedFilterChange('price_range.max', e.target.value)}
                  placeholder="999999.00"
                />
              </div>

              <div className="space-y-2">
                <Label>最小刃径</Label>
                <Input
                  type="number"
                  value={filters.tip_diameter_range?.min || ''}
                  onChange={(e) => onAdvancedFilterChange('tip_diameter_range.min', e.target.value)}
                  placeholder="0.0"
                />
              </div>

              <div className="space-y-2">
                <Label>最大刃径</Label>
                <Input
                  type="number"
                  value={filters.tip_diameter_range?.max || ''}
                  onChange={(e) => onAdvancedFilterChange('tip_diameter_range.max', e.target.value)}
                  placeholder="999.0"
                />
              </div>

              <div className="space-y-2">
                <Label>最小总长</Label>
                <Input
                  type="number"
                  value={filters.total_length_range?.min || ''}
                  onChange={(e) => onAdvancedFilterChange('total_length_range.min', e.target.value)}
                  placeholder="0.0"
                />
              </div>

              <div className="space-y-2">
                <Label>最大总长</Label>
                <Input
                  type="number"
                  value={filters.total_length_range?.max || ''}
                  onChange={(e) => onAdvancedFilterChange('total_length_range.max', e.target.value)}
                  placeholder="999.0"
                />
              </div>

              <div className="space-y-2">
                <Label>最小角度 (°)</Label>
                <Input
                  type="number"
                  value={filters.degree_range?.min || ''}
                  onChange={(e) => onAdvancedFilterChange('degree_range.min', e.target.value)}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label>最大角度 (°)</Label>
                <Input
                  type="number"
                  value={filters.degree_range?.max || ''}
                  onChange={(e) => onAdvancedFilterChange('degree_range.max', e.target.value)}
                  placeholder="180"
                />
              </div>

              {/* Inventory-specific fields */}
              {pageType === 'inventory' && (
                <>
                  <div className="space-y-2">
                    <Label>最小库存数量</Label>
                    <Input
                      type="number"
                      value={filters.min_stock || ''}
                      onChange={(e) => onAdvancedFilterChange('min_stock', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>最大库存数量</Label>
                    <Input
                      type="number"
                      value={filters.max_stock || ''}
                      onChange={(e) => onAdvancedFilterChange('max_stock', e.target.value)}
                      placeholder="999999"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>位置</Label>
                    <Select
                      value={filters.location || 'all'}
                      onValueChange={(value) => onAdvancedFilterChange('location', value === 'all' ? '' : value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="全部位置" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">全部位置</SelectItem>
                        <SelectItem value="warehouse_a">仓库A</SelectItem>
                        <SelectItem value="warehouse_b">仓库B</SelectItem>
                        <SelectItem value="production">生产车间</SelectItem>
                        <SelectItem value="quality_control">质量控制</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {/* Admin Trade Type Filter */}
              {userRole === 'admin' && (
                <div className="space-y-2">
                  <Label>贸易类型 (管理员)</Label>
                  <Select
                    value={tradeTypeFilter}
                    onValueChange={onTradeTypeFilterChange}
                  >
                    <SelectTrigger className="border-blue-500">
                      <SelectValue placeholder="全部" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        全部{pageType === 'products' ? '产品' : pageType === 'quotations' ? '报价单' : '订单'}
                      </SelectItem>
                      <SelectItem value="domestic">仅国内</SelectItem>
                      <SelectItem value="international">仅国际出口</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Custom fields for specific pages */}
              {customAdvancedFields.map((field, index) => (
                <div key={index} className="space-y-2">
                  {field.component}
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};

export default AdvancedSearch;
